import { TWasmId } from "~/interfaces";
import { IStoreItem, IWasmStoreGeometry, IWasmStoreMarker } from "./store/interfaces";
import Wasm from "./wasm";
import WasmMapMarker from "./entities/wasm-map-marker";
import WasmMapGeometry from "./entities/wasm-map-geometry";
import { getRemovedIds } from "./utils";
import { watchMapContentChanges } from "~/state/map-content/utils";
import { store } from "~/state/store";
import WasmMapMarkerSharedPosition from "./entities/wasm-map-marker-shared-position";

const NO_FLICKERING_MARKER_CREATE = true;

export default class WasmStoreMapping {
	#wasm: Wasm;

	#markerStoreIdMapping: {
		// klid data id => zmena + obj
		[key: IStoreItem["id"]]: {
			marker: WasmMapMarker | WasmMapMarkerSharedPosition;
			data: IWasmStoreMarker;
			wasmIds: Array<TWasmId>;
		};
	};

	#geometryStoreIdMapping: {
		// klid data id => zmena + obj
		[key: IStoreItem["id"]]: {
			geometry: WasmMapGeometry;
			data: IWasmStoreGeometry;
			wasmIds: Array<TWasmId>;
		};
	};

	// klic data id z wasm jako cislo -> data id str
	// wasm -> store, sdilene pro markery + geometrie
	#wasmToStoreMapping: {
		[key: TWasmId]: IStoreItem["id"];
	};

	constructor(wasm: Wasm) {
		this.#wasm = wasm;
		this.#markerStoreIdMapping = {};
		this.#geometryStoreIdMapping = {};
		this.#wasmToStoreMapping = {};

		this.#build();
	}

	/**
	 * Mapovani wasm entity id na store id
	 */
	getWasmToStoreId(wasmId: TWasmId) {
		return this.#wasmToStoreMapping[wasmId] || 0;
	}

	/**
	 * Mapovani store id entity na wasm ids
	 */
	getStoreToWasmIds(storeId: IStoreItem["id"]): Array<TWasmId> {
		if (storeId in this.#markerStoreIdMapping) {
			return this.#markerStoreIdMapping[storeId].wasmIds;
		} else if (storeId in this.#geometryStoreIdMapping) {
			return this.#geometryStoreIdMapping[storeId].wasmIds;
		}

		return [];
	}

	get stats() {
		const output = {
			// viditelnych/celkem...
			allStats: [0, 0],
			markersStats: [0, 0],
			markers: {},
			geometriesStats: [0, 0],
			geometries: {},
			popupsStats: [0, 0],
			popups: {},
		};

		const markerValues = Object.values(this.#markerStoreIdMapping);

		for (const marker of markerValues) {
			const type = marker.data.type;
			let key = "markers";
			const isVisible = marker.data.visible;

			if (type.includes("popup")) {
				key = "popups";
				output.popupsStats[1]++;

				if (isVisible) {
					output.popupsStats[0]++;
				}
			} else {
				output.markersStats[1]++;

				if (isVisible) {
					output.markersStats[0]++;
				}
			}

			if (!(type in output[key])) {
				output[key][type] = {
					count: 0,
					items: [],
				};
			}

			if (isVisible) {
				output.allStats[0]++;
			}

			output.allStats[1]++;
			output[key][type].count++;
			output[key][type].items.push(marker.data);
		}

		const geometryValues = Object.values(this.#geometryStoreIdMapping);

		for (const geometry of geometryValues) {
			const type = geometry.data.type;

			if (!(type in output.geometries)) {
				output.geometries[type] = {
					count: 0,
					items: [],
				};
			}

			if (geometry.data.visible) {
				output.geometriesStats[0]++;
				output.allStats[0]++;
			}

			output.allStats[1]++;
			output.geometriesStats[1]++;
			output.geometries[type].count++;
			output.geometries[type].items.push(geometry.data);
		}

		return output;
	}

	#build() {
		// init stav
		const curState = store.getState().mapContent;
		const initMarkers = curState.markers;
		const initGeometries = curState.geometries;

		if (initMarkers.length > 0) {
			this.#markersChange(initMarkers.map(item => item.id), initMarkers);
		}

		if (initGeometries.length > 0) {
			this.#geometriesChange(initGeometries.map(item => item.id), initGeometries);
		}

		// zmeny stavu
		watchMapContentChanges({
			store,
			onMarkersChange: (ids, markers) => {
				this.#markersChange(ids, markers);
			},
			onGeometriesChange: (ids, geometries) => {
				this.#geometriesChange(ids, geometries);
			},
		});
	}

	#addMarker(markerData: IWasmStoreMarker) {
		const wasmMarker = this.#wasm.createMarker(markerData);
		const wasmIds = wasmMarker.wasmIds;

		this.#markerStoreIdMapping[markerData.id] = {
			data: structuredClone(markerData),
			marker: wasmMarker,
			wasmIds,
		};

		for (const wasmId of wasmIds) {
			this.#wasmToStoreMapping[wasmId] = markerData.id;
		}
	}

	#removeMarker(markerId: IStoreItem["id"]) {
		if (markerId in this.#markerStoreIdMapping) {
			const curData = this.#markerStoreIdMapping[markerId];
			const wasmIds = curData.marker.wasmIds;

			curData.marker.destroy();

			for (const wasmId of wasmIds) {
				delete this.#wasmToStoreMapping[wasmId];
			}

			delete this.#markerStoreIdMapping[markerId];
		}
	}

	#markersChange(affectedIds: Array<IStoreItem["id"]>, markers: Array<IWasmStoreMarker>) {
		const newIds: Array<IStoreItem["id"]> = [];

		// pridani + editace
		for (const marker of markers) {
			newIds.push(marker.id);

			// preskocime
			if (affectedIds.length > 0 && !affectedIds.includes(marker.id)) {
				continue;
			}

			if (marker.id in this.#markerStoreIdMapping) {
				const curData = this.#markerStoreIdMapping[marker.id];
				let wasRecreated = false;

				if (curData.data.lastChange !== marker.lastChange) {
					if (curData.data.lon !== marker.lon || curData.data.lat !== marker.lat) {
						curData.marker.setPosition(marker.lon, marker.lat);
					}

					if (curData.data.visual !== marker.visual) {
						curData.marker.setVisual(marker.visual);
					}

					if (curData.data.visible !== marker.visible) {
						curData.marker.setVisibility(marker.visible);
					}

					if (curData.data.angle !== marker.angle) {
						curData.marker.setAngle(marker.angle);
					}

					if (curData.data.bringToTheFront !== marker.bringToTheFront) {
						curData.marker.setBringToTheFront(marker.bringToTheFront);
					}

					// znovu vytvorime - todor jestli bude potreba
					if (curData.data.callback !== marker.callback) {
						if (NO_FLICKERING_MARKER_CREATE) {
							const wasmIds = curData.wasmIds.slice();
							const curMarker = curData.marker;

							this.#addMarker(marker);

							for (const wasmId of wasmIds) {
								delete this.#wasmToStoreMapping[wasmId];
							}

							setTimeout(() => {
								curMarker.destroy();
							}, 100);
						} else {
							this.#removeMarker(marker.id);
							this.#addMarker(marker);
						}

						wasRecreated = true;
					}

					// aktualizujeme
					if (!wasRecreated) {
						curData.data = structuredClone(marker);
					}
				}
			} else {
				// neexistuje
				this.#addMarker(marker);
			}
		}

		// mazani
		const allIds = Object.keys(this.#markerStoreIdMapping).map(item => parseFloat(item));
		const removed = getRemovedIds(allIds, newIds);

		for (const removeId of removed) {
			this.#removeMarker(removeId);
		}
	}

	#addGeometry(geomData: IWasmStoreGeometry) {
		const wasmGeometry = this.#wasm.createGeometry(geomData);
		const wasmIds = wasmGeometry.wasmIds;

		this.#geometryStoreIdMapping[geomData.id] = {
			data: structuredClone(geomData),
			geometry: wasmGeometry,
			wasmIds,
		};

		for (const wasmId of wasmIds) {
			this.#wasmToStoreMapping[wasmId] = geomData.id;
		}
	}

	#removeGeometry(geomId: IStoreItem["id"]) {
		if (geomId in this.#geometryStoreIdMapping) {
			const curData = this.#geometryStoreIdMapping[geomId];
			const wasmIds = curData.geometry.wasmIds;

			curData.geometry.destroy();

			for (const wasmId of wasmIds) {
				delete this.#wasmToStoreMapping[wasmId];
			}

			delete this.#geometryStoreIdMapping[geomId];
		}
	}

	#geometriesChange(affectedIds: Array<IStoreItem["id"]>, geometries: Array<IWasmStoreGeometry>) {
		const newIds: Array<IStoreItem["id"]> = [];

		// pridani + editace
		for (const geometry of geometries) {
			newIds.push(geometry.id);

			// preskocime
			if (affectedIds.length > 0 && !affectedIds.includes(geometry.id)) {
				continue;
			}

			if (geometry.id in this.#geometryStoreIdMapping) {
				const curData = this.#geometryStoreIdMapping[geometry.id];

				if (curData.data.lastChange !== geometry.lastChange) {
					if (curData.data.lastGeomChange !== geometry.lastGeomChange) {
						curData.geometry.updatePoints(geometry.points);
						curData.geometry.updateStrPoints(geometry.strPoints);
					}

					if (curData.data.visual !== geometry.visual) {
						curData.geometry.setVisual(geometry.visual);
					}

					if (curData.data.visible !== geometry.visible) {
						curData.geometry.setVisibility(geometry.visible);
					}

					if (curData.data.bringToTheFront !== geometry.bringToTheFront) {
						curData.geometry.setBringToTheFront(geometry.bringToTheFront);
					}

					// aktualizujeme
					curData.data = structuredClone(geometry);
				}
			} else {
				// neexistuje
				this.#addGeometry(geometry);
			}
		}

		// mazani
		const allIds = Object.keys(this.#geometryStoreIdMapping).map(item => parseFloat(item));
		const removed = getRemovedIds(allIds, newIds);

		for (const removeId of removed) {
			this.#removeGeometry(removeId);
		}
	}
}
